import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback } from 'react';
import { Box, Button, Divider, Theme, Typography, useMediaQuery } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { DateRange } from './DateRangeSelect.component';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';

/**
 * Interface pour les props du composant NextDaysStripSelect
 */
interface NextDaysStripSelectProps {
  /** Plage de dates actuellement sélectionnée */
  value: DateRange;
  /** Fonction appelée lors du changement de date */
  onChange: (value: Dayjs, valueEnd?: Dayjs) => any;
}
const useStyles = makeStyles((theme: Theme) => ({
  root: {
    '& > *': {
      margin: theme.spacing(2, 1, 0, 1),
    },
    [theme.breakpoints.down('xs')]: {
      marginRight: theme.spacing(1),
      padding: theme.spacing(0.5, 1),
    },
  },
}));

/**
 * Composant interne pour afficher un jour dans la bande de sélection
 * 
 * @param label Label du jour à afficher
 * @param selected Indique si ce jour est sélectionné
 * @param onClick Fonction appelée lors du clic (optionnelle)
 * @param classes Classes CSS du composant
 */
const OneDay = ({
  label,
  selected,
  onClick,
  classes
}: {
  label: string;
  selected: boolean;
  onClick?: any;
  classes: ClassNameMap<any>;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  return (
    <Button onClick={onClick} className={classes.menuButton}>
      <Typography
        variant={isMobile ? "subtitle1" : "h5"}
        gutterBottom
        color={selected ? 'textPrimary' : 'textSecondary'}
        style={{ opacity: selected ? 1 : 0.7 }}
      >
        {label}
      </Typography>
    </Button>
  );
};
/**
 * Composant de sélection de plage de jours avec options prédéfinies
 * 
 * Ce composant affiche :
 * - Options de sélection rapide (Tous, Aujourd'hui, Demain, Après-demain)
 * - Gestion des plages de dates et des jours uniques
 * - Design responsive pour mobile et desktop
 * - Séparateurs visuels entre les options
 * 
 * Logique métier :
 * - "Tous" sélectionne de aujourd'hui jusqu'à la fin de l'année
 * - Les autres options sélectionnent un jour unique
 * - Détection automatique du type de sélection (plage vs jour unique)
 * - Callbacks optimisés avec useCallback pour les performances
 * 
 * @param value Plage de dates actuellement sélectionnée
 * @param onChange Fonction appelée lors du changement de date
 * @returns Composant React de sélection de plage de jours
 */
export const NextDaysStripSelect = ({
  value,
  onChange,
}: NextDaysStripSelectProps) => {
  const classes = useStyles();

  const onlyOneDayRange = value.end && value.start?.isSame(value.end, 'day');
  const today = dayjs();
  const tomorrow = today.add(1, 'day');
  const afterTomorrow = today.add(2, 'day');

  const selectToday = useCallback(() => onChange(today), [onChange, today]);
  const selectTomorrow = useCallback(() => onChange(tomorrow), [
    onChange,
    tomorrow,
  ]);
  const selectAfterTommorow = useCallback(() => onChange(afterTomorrow), [
    onChange,
    afterTomorrow,
  ]);

  const selectAllFutur = useCallback(() => onChange(today, dayjs().endOf('year')), [
    onChange,
    today
  ]);

  return (
    <Box display="flex" className={classes.root}>
      <OneDay
        label={"Tous"}
        selected={!Boolean(onlyOneDayRange) && today.isSame(value.start!, 'day') && dayjs().endOf('year').isSame(value.end!, 'day')}
        onClick={selectAllFutur}
        classes={classes}
      />
      <Divider orientation="vertical" flexItem />
      <OneDay
        label={"Aujourd'hui"}
        selected={Boolean(onlyOneDayRange) && today.isSame(value.start!, 'day')}
        onClick={selectToday}
        classes={classes}
      />
      <Divider orientation="vertical" flexItem />
      <OneDay
        label={'Demain'}
        selected={
          Boolean(onlyOneDayRange) && tomorrow.isSame(value.start!, 'day')
        }
        onClick={selectTomorrow}
        classes={classes}
      />
      <Divider orientation="vertical" flexItem />
      <OneDay
        label={'Apr. demain'}
        selected={
          Boolean(onlyOneDayRange) && afterTomorrow.isSame(value.start!, 'day')
        }
        onClick={selectAfterTommorow}
        classes={classes}
      />
      <Divider orientation="vertical" flexItem />
    </Box>
  );
};
